import React, { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import AppFooter from './AppFooter';
import { TooltipProvider } from '../components/ui/tooltip';
import { Sheet, SheetContent } from '../components/ui/sheet';

/**
 * AppShell — shared chrome (sidebar + topbar + footer) for all authenticated routes.
 * Auth is enforced here; un-authenticated visitors are redirected to /auth.
 * Renders `children` when passed directly (RootRoute), else the route <Outlet />.
 */
const AppShell = ({ children }) => {
  const { user, loading } = useAuth();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="min-h-screen flex bg-background text-foreground">
        {/* Desktop / tablet sidebar */}
        <Sidebar variant="desktop" />

        {/* Mobile sidebar (drawer) */}
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-72 p-0 border-r border-border bg-card">
            <Sidebar variant="mobile" onNavigate={() => setMobileSidebarOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0">
          <TopBar onOpenMobileSidebar={() => setMobileSidebarOpen(true)} />
          <div className="flex-1 min-w-0">
            {children ?? <Outlet />}
          </div>
          <AppFooter />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AppShell;
